// Comms feed for the Intelligence Board's left column.
//
// Two streams:
//  1) Sector chatter — chronological feed of who said what across all sectors
//  2) Per-entity threads — a focused thread when something is selected
//     (per-driver private thread, per-flight crew thread, per-gate gate thread)
//
// Real implementation would be live; this is a believable seed.

import { DRIVERS, GATE_STATE } from './mockLiveState';

// Find a few drivers to attribute messages to
const driver = (i) => DRIVERS[i % DRIVERS.length];

// Quick author tag generator
function tag(d) {
  return { id: d.id, name: d.name, badge: d.badge, kind: 'driver' };
}
const DISPATCH = { id: 'dispatch', name: 'Dispatch', badge: '—', kind: 'dispatch' };
const SYSTEM   = { id: 'system',   name: 'System',   badge: '—', kind: 'system' };

// Sector chatter — chronological, mixed authors. Realistic-flavored
// content the dispatcher would actually see come across: ETA slips,
// gate changes, missing/late bags, tug issues, hot connections.
export const SECTOR_CHATTER = [
  { id: 'c1',  time: '11:58', author: SYSTEM,           body: 'WN 1854 ETA pushed +18 min — new ETA 12:24.' },
  { id: 'c2',  time: '11:56', author: DISPATCH,         body: 'Heads up — WN 2109 gate change C44 → C38. Update your runs.' },
  { id: 'c3',  time: '11:55', author: tag(driver(2)),   body: 'Missing 1 bag on WN 1296 transfer to BOI. Re-checking the cart.' },
  { id: 'c4',  time: '11:53', author: tag(driver(11)),  body: 'Tug T-4127 brakes spongy, pulling it out of rotation.' },
  { id: 'c5',  time: '11:51', author: SYSTEM,           body: 'WN 3257 ETA +9 min — new ETA 12:08.' },
  { id: 'c6',  time: '11:49', author: tag(driver(7)),   body: 'Hot bag found on the cart — late tag. On the way to C56 now.' },
  { id: 'c7',  time: '11:46', author: DISPATCH,         body: 'WN 4534 inbound — only 4 bags coming off, expedite.' },
  { id: 'c8',  time: '11:44', author: SYSTEM,           body: 'Gate change: WN 1488 → C53.' },
  { id: 'c9',  time: '11:42', author: tag(driver(20)),  body: 'Need a tug at C40, ours just got pulled.' },
  { id: 'c10', time: '11:40', author: DISPATCH,         body: 'Anyone free to cover C61? Driver clocking out at 12:00.' },
  { id: 'c11', time: '11:38', author: tag(driver(33)),  body: 'I can take it — finishing up at C58.' },
  { id: 'c12', time: '11:36', author: SYSTEM,           body: 'WN 2756 cancelled — bags routing to Reroutes (C45).' },
  { id: 'c13', time: '11:33', author: tag(driver(41)),  body: 'C24 jet bridge stuck again, ground crew working it.' },
  { id: 'c14', time: '11:30', author: DISPATCH,         body: 'Reminder: 12:30 bank pulse meeting on Channel 2.' },
];

// Per-entity threads — keyed by entity.id (driver id, flight number, or gate id).
// When the dispatcher selects an entity, the panel switches to that thread.
export function threadFor(selected) {
  if (!selected) return null;

  if (selected.kind === 'driver') {
    const d = DRIVERS.find(x => x.id === selected.id);
    if (!d) return null;
    return {
      title: `Thread · ${d.name}`,
      subtitle: `Badge ${d.badge} · Seniority ${d.seniority}`,
      messages: [
        { id: 't1', time: '11:56', author: DISPATCH, body: `Next run: ${d.gateId || 'C39'}, expedite — flight pushes in 18 min.` },
        { id: 't2', time: '11:54', author: tag(d),   body: 'Copy. Two bags missing off the previous one, leaving them with the lead.' },
        { id: 't3', time: '11:50', author: DISPATCH, body: 'Tug check before you go — T-3215 was flagged this morning.' },
        { id: 't4', time: '11:42', author: tag(d),   body: 'Tug clean. Heading over now.' },
        { id: 't5', time: '11:30', author: SYSTEM,   body: 'Break clock paused — covering C40.' },
      ],
    };
  }

  if (selected.kind === 'flight') {
    return {
      title: `Thread · ${selected.id}`,
      subtitle: 'Per-flight coordination',
      messages: [
        { id: 'f1', time: '11:58', author: SYSTEM,   body: `${selected.id} ETA pushed +12 min — coordinate with downstream.` },
        { id: 'f2', time: '11:53', author: DISPATCH, body: 'Hot bag inbound to BOI — confirm driver is at the gate before doors open.' },
        { id: 'f3', time: '11:46', author: tag(driver(7)), body: 'At the gate. Tug positioned, waiting on jet bridge.' },
        { id: 'f4', time: '11:38', author: SYSTEM,   body: 'Bag count revised: 134 bags (was 142).' },
        { id: 'f5', time: '11:30', author: DISPATCH, body: 'Two FWD-only — load priority on bin A.' },
      ],
    };
  }

  if (selected.kind === 'gate') {
    const s = GATE_STATE.get(selected.id);
    return {
      title: `Thread · Gate ${selected.id}`,
      subtitle: s?.flightNumber ? `Hosting ${s.flightNumber}` : 'No active flight',
      messages: [
        { id: 'g1', time: '11:55', author: SYSTEM,   body: `Inbound at gate ${selected.id} pushing back — driver clear.` },
        { id: 'g2', time: '11:48', author: DISPATCH, body: `Next inbound to ${selected.id} in 24 min — keep tug pre-positioned.` },
        { id: 'g3', time: '11:36', author: tag(driver(20)), body: 'Belt loader is sluggish, taking a few extra min on the download.' },
        { id: 'g4', time: '11:28', author: SYSTEM,   body: `Gate ${selected.id} bridge cleared. Ready for next aircraft.` },
      ],
    };
  }

  return null;
}
